import {
  Button,
  Card,
  Grid,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  Chip,
  IconButton,
  Box
} from '@material-ui/core';
import {
  GetApp as ExportIcon,
  Publish as ImportIcon,
  SportsEsports as GameIcon,
  Quiz as QuestionIcon,
  Add as AddIcon,
  Save as SaveIcon,
  ArrowDropDown as DropDownIcon,
  Close as CloseIcon
} from '@material-ui/icons';
import { useFormik } from 'formik';
import Fuse from 'fuse.js';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import useApi from 'src/api/api';
import ClassSelector from 'src/components/ClassSelector';
import EditDialog from 'src/components/EditDialog';
import NoRecordItem from 'src/components/NoRecordItem';
import SearchInput from 'src/components/SearchInput';
import utils from 'src/utils/utils';
import { SubjectSelector } from 'src/components/SubjectSelector';
import LayoutContainer from 'src/layout/LayoutContainer';
import { useLocale } from 'src/provider/LocaleProvider';
import formikProps from 'src/utils/formikProps';
import { GameListCard } from './GameListCard';

// const prettyBytes = require('pretty-bytes');
const yup = require('yup');

// Helper function to format dates for display in date inputs
const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return '';

  // If it's already in YYYY-MM-DD format, return as is
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  // If it's a date string or Date object, convert to YYYY-MM-DD
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  } catch (error) {
    return '';
  }
};

// Helper function to format dates for API submission
const formatDateForAPI = (dateValue) => {
  if (!dateValue) return '';

  // If it's already in YYYY-MM-DD format, return as is
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  // If it's a date string or Date object, convert to YYYY-MM-DD
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  } catch (error) {
    return '';
  }
};

export default function GameList({ snack }) {
  const nav = useNavigate();
  const { t } = useLocale();
  const api = useApi();
  const [dialogMode, setDialogMode] = useState();
  const [tab, setTab] = useState('single');
  const [selectedSubject, setSelectedSubject] = useState('ALL');
  const [selectedClass, setSelectedClass] = useState();

  const [gameList, setGameList] = useState([]);
  const [gameListBySubject, setGameListBySubject] = useState([]);
  const [search, setSearch] = useState('');
  const [filterOptions, setFilterOptions] = useState([]);

  // Dropdown menu states
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [importAnchorEl, setImportAnchorEl] = useState(null);
  const isExportMenuOpen = Boolean(exportAnchorEl);
  const isImportMenuOpen = Boolean(importAnchorEl);

  const deboundedSearch = _.debounce((value) => {
    setSearch(value);
  }, 500);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    api.getGameList().then((res) => {
      setGameList(res.data);
    });

    // Load filter options for the form
    api.getOrganizationFilters().then((res) => {
      setFilterOptions(res.data);
    });
  }, []);

  const fuse = useMemo(() => {
    return new Fuse(gameList, {
      isCaseSensitive: false,
      useExtendedSearch: true,
      keys: ['subjects._id', 'filters._id', 'name', 'description']
    });
  }, [gameList]);

  useEffect(() => {
    const fuesParams = [];

    // Apply name filter using Fuse.js
    selectedClass?._id &&
      fuesParams.push({ 'filters._id': `'${selectedClass._id}` });

    search &&
      fuesParams.push({ $or: [{ name: search }, { description: search }] });

    selectedSubject !== 'ALL' &&
      selectedSubject !== 'NONE' &&
      fuesParams.push({ 'subjects._id': `'${selectedSubject}` });

    let fuseResult = fuesParams.length
      ? fuse.search({ $and: fuesParams }).map((item) => item.item)
      : gameList;

    if (selectedSubject === 'NONE') {
      fuseResult = fuseResult.filter((g) => _.isEmpty(g.subjects));
    }

    if (selectedClass?.value === 'NONE') {
      fuseResult = fuseResult.filter((g) => _.isEmpty(g.filters));
    }

    // Sort by order number (ascending)
    fuseResult = _.orderBy(fuseResult, ['order'], ['asc']);

    setGameListBySubject(fuseResult);
  }, [selectedSubject, gameList, selectedClass, search, fuse]);

  useEffect(() => {
    if (dialogMode) formik.resetForm();
  }, [dialogMode]);

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      gameType: 'mc',
      order: 0,
      featureStart: '',
      featureEnd: '',
      pointCorrect: 0, // Set default value instead of null
      pointIncorrect: 0, // Set default value instead of null
      pointExchangeRate: 1, // Set default value instead of null
      isActive: true,
      filters: [],
      subjects: []
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      // 必填欄位 (Required fields)
      name: yup.string().required(t('yup_required')),
      gameType: yup
        .string()
        .oneOf(['mc', 'interactive'])
        .required(t('yup_required')),
      isActive: yup.bool().required(t('yup_required')),
      featureStart: yup.string().optional(),
      featureEnd: yup
        .string()
        .optional()
        .test('end-after-start', t('end_date_after_start'), function (value) {
          const { featureStart } = this.parent;
          if (!featureStart || !value) return true;
          return new Date(value) >= new Date(featureStart);
        }),

      // Required fields for MC games
      pointCorrect: yup.number().when('gameType', (gameType, schema) => {
        if (gameType === 'mc') {
          return schema.min(0).required(t('yup_required'));
        }
        return schema.nullable();
      }),
      pointIncorrect: yup.number().when('gameType', (gameType, schema) => {
        if (gameType === 'mc') {
          return schema.min(0).required(t('yup_required'));
        }
        return schema.nullable();
      }),
      pointExchangeRate: yup.number().when('gameType', (gameType, schema) => {
        if (gameType === 'mc') {
          return schema.min(0).required(t('yup_required'));
        }
        return schema.nullable();
      }),

      // 可選欄位 (Optional fields)
      order: yup.number().min(0, t('yup_min_zero')), // Optional - removed required()
      description: yup.string(), // Optional field
      filters: yup.array(), // Optional field - 篩選器/班級
      subjects: yup.array() // Optional field - 科目
    }),
    onSubmit: (values, helper) => {
      console.log('Form submitted with values:', values);

      // Format dates for API submission
      const submissionValues = {
        ...values,
        featureStart: formatDateForAPI(values.featureStart),
        featureEnd: formatDateForAPI(values.featureEnd)
      };

      console.log('Formatted values for API:', submissionValues);

      api
        .createGame(submissionValues)
        .then(async (res) => {
          console.log('Game created successfully:', res.data);
          setDialogMode(null); // Close the dialog
          nav(`/admin/games/${res.data._id}`);
        })
        .catch((e) => {
          console.error('Error creating game:', e);
          snack.error(t('create_game_failed') || 'Failed to create game');
        })
        .finally(() => {
          helper.setSubmitting(false);
        });
    }
  });
  const hasPointConfig = formik.values.gameType === 'mc';

  const gameListByType = useMemo(() => {
    return _.groupBy(gameListBySubject, (g) => g.gameType);
  }, [gameListBySubject]);
  const [filteredList, setFilteredList] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (tab === 'multi') {
      setFilteredList(
        _.orderBy(_.get(gameListByType, 'interactive', []), 'order', 'asc')
      );
    }
    if (tab === 'single') {
      setFilteredList(
        _.orderBy(_.get(gameListByType, 'mc', []), 'order', 'asc')
      );
    }
  }, [tab, gameListByType]);

  const setIsGameActiveInList = (id) => {
    setFilteredList(
      _.map(filteredList, (game) =>
        game._id === id ? { ...game, isActive: !game.isActive } : game
      )
    );
  };

  const updateGameStatusBulk = () => {
    setIsSaving(true);
    const data = _.map(filteredList, (game) => {
      return { _id: game._id, isActive: game.isActive };
    });
    api
      .updateGameStatusBulk(data)
      .then((_) => {
        snack.open(t('upload_success'));
      })
      .catch((_) => {
        snack.error();
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleExport = () => {
    // Export all games (both single and multi) together, sorted by order
    const allGames = [
      ..._.get(gameListByType, 'mc', []),
      ..._.get(gameListByType, 'interactive', [])
    ];
    const sortedGames = _.orderBy(allGames, ['order'], ['asc']);

    const exportData = sortedGames.map((game) => ({
      id: game._id,
      name: game.name,
      description: game.description || '',
      gameType: game.gameType,
      order: game.order || 0,
      featureStart: formatDateForDisplay(game.featureStart),
      featureEnd: formatDateForDisplay(game.featureEnd),
      pointCorrect: game.pointCorrect || 0,
      pointIncorrect: game.pointIncorrect || 0,
      pointExchangeRate: game.pointExchangeRate || 0,
      isActive: game.isActive ? 'Active' : 'Inactive',
      filters:
        game.filters
          ?.map((f) => f.name?.en || f.name?.zh || f.name)
          .join(', ') || '',
      subjects:
        game.subjects
          ?.map((s) => s.name?.en || s.name?.zh || s.name)
          .join(', ') || ''
    }));

    utils.exportCsv(
      exportData,
      [
        { label: 'ID', value: 'id' },
        { label: 'Name', value: 'name' },
        { label: 'Description', value: 'description' },
        { label: 'Game_Type', value: 'gameType' },
        { label: 'Order', value: 'order' },
        { label: 'Feature_Start', value: 'featureStart' },
        { label: 'Feature_End', value: 'featureEnd' },
        { label: 'Point_Correct', value: 'pointCorrect' },
        { label: 'Point_Incorrect', value: 'pointIncorrect' },
        { label: 'Point_Exchange_Rate', value: 'pointExchangeRate' },
        { label: 'Is_Active', value: 'isActive' },
        { label: 'Filters', value: 'filters' },
        { label: 'Subjects', value: 'subjects' }
      ],
      'games_data'
    );
  };

  // Export menu handlers
  const handleExportMenuOpen = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportGame = () => {
    handleExport();
    handleExportMenuClose();
  };

  const handleExportQuestion = async () => {
    // Export questions with same format as GameQuestionUpload
    try {
      const questionsRes = await api.getAllGameQuestions();
      const allQuestions = questionsRes.data || [];

      // Format questions for export - same format as GameQuestionUpload
      const exportData = allQuestions.map((question) => {
        const options = question.options || [];

        // Find all correct answers
        let correctAnswers = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i]?.isCorrect || options[i]?.isAns) {
            correctAnswers.push(String.fromCharCode(65 + i)); // A=65, B=66, C=67, D=68
          }
        }

        return {
          question_id: question._id || question.id || '',
          game_id: question.game || '',
          question_text: question.text || '',
          option_a: options[0]?.text || '',
          option_b: options[1]?.text || '',
          option_c: options[2]?.text || '',
          option_d: options[3]?.text || '',
          correct_answer: correctAnswers.join(',')
        };
      });

      // Export using same format as GameQuestionUpload
      utils.exportCsv(
        exportData,
        [
          { label: 'question_id', value: 'question_id' },
          { label: 'Game_ID', value: 'game_id' },
          { label: 'Question_Text', value: 'question_text' },
          { label: 'Option_A', value: 'option_a' },
          { label: 'Option_B', value: 'option_b' },
          { label: 'Option_C', value: 'option_c' },
          { label: 'Option_D', value: 'option_d' },
          { label: 'Correct_Answer', value: 'correct_answer' }
        ],
        'questions_data'
      );

      snack?.success?.(t('export_success')) ||
        console.log('Export questions completed');
    } catch (error) {
      console.error('Export questions error:', error);
      snack?.error?.(t('export_failed')) || console.error('Export failed');
    } finally {
      handleExportMenuClose();
    }
  };

  // Import menu handlers
  const handleImportMenuOpen = (event) => {
    setImportAnchorEl(event.currentTarget);
  };

  const handleImportMenuClose = () => {
    setImportAnchorEl(null);
  };

  const handleImportGame = () => {
    nav('/admin/games/upload');
    handleImportMenuClose();
  };

  const handleImportQuestion = () => {
    // Navigate to question import/upload page
    nav('/admin/games/questions/upload');
    handleImportMenuClose();
  };

  const handleAddNewGame = () => {
    setDialogMode('add');
  };

  return (
    <LayoutContainer title={t('game.game')}>
      <Grid container spacing={2} mb={2}>
        {/* Filter Section */}
        <Grid item xs={12}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="flex-start"
            wrap="wrap"
          >
            <Grid item xs={12} sm={4} md={3}>
              <Box display="flex" alignItems="center">
                <SearchInput
                  setSearch={deboundedSearch}
                  fullWidth
                  sx={{
                    minWidth: 0,
                    height: '56px',
                    width: '100%',
                    flexGrow: 1
                  }}
                />
                {search && CloseButton({ onClick: () => setSearch('') })}
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Box display="flex" alignItems="center">
                <ClassSelector
                  selected={selectedClass}
                  setSelected={setSelectedClass}
                  hasUncategorized={true}
                  fullWidth
                  sx={{
                    minWidth: 0,
                    height: '56px',
                    width: '100%',
                    flexGrow: 1
                  }}
                />
                {selectedClass && (
                  <CloseButton onClick={() => setSelectedClass()} />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Box display="flex" alignItems="center">
                <SubjectSelector
                  selected={selectedSubject}
                  setSelected={setSelectedSubject}
                  fullWidth
                  sx={{
                    minWidth: 0,
                    height: '56px',
                    width: '100%',
                    flexGrow: 1
                  }}
                />
                {selectedSubject !== 'ALL' && (
                  <CloseButton onClick={() => setSelectedSubject('ALL')} />
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* Button Section */}
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <Button
                onClick={handleExportMenuOpen}
                variant="contained"
                fullWidth
                size="medium"
                startIcon={<ExportIcon />}
                endIcon={<DropDownIcon />}
                aria-controls={isExportMenuOpen ? 'export-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isExportMenuOpen ? 'true' : undefined}
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': { backgroundColor: '#1565c0' },
                  fontWeight: 600
                }}
              >
                {t('export')}
              </Button>
              <Menu
                id="export-menu"
                anchorEl={exportAnchorEl}
                open={isExportMenuOpen}
                onClose={handleExportMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'export-button'
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 180,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    borderRadius: 2,
                    border: '1px solid rgba(0,0,0,0.12)'
                  }
                }}
              >
                <MenuItem
                  onClick={handleExportGame}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <GameIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('game')} />
                </MenuItem>
                <MenuItem
                  onClick={handleExportQuestion}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <QuestionIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('question')} />
                </MenuItem>
              </Menu>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button
                onClick={handleImportMenuOpen}
                variant="contained"
                fullWidth
                size="medium"
                startIcon={<ImportIcon />}
                endIcon={<DropDownIcon />}
                aria-controls={isImportMenuOpen ? 'import-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isImportMenuOpen ? 'true' : undefined}
                sx={{
                  backgroundColor: '#2e7d32',
                  '&:hover': { backgroundColor: '#1b5e20' },
                  fontWeight: 600
                }}
              >
                {t('import')}
              </Button>
              <Menu
                id="import-menu"
                anchorEl={importAnchorEl}
                open={isImportMenuOpen}
                onClose={handleImportMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'import-button'
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 180,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    borderRadius: 2,
                    border: '1px solid rgba(0,0,0,0.12)'
                  }
                }}
              >
                <MenuItem
                  onClick={handleImportGame}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <GameIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('game')} />
                </MenuItem>
                <MenuItem
                  onClick={handleImportQuestion}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <QuestionIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('question')} />
                </MenuItem>
              </Menu>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button
                onClick={handleAddNewGame}
                variant="contained"
                fullWidth
                size="medium"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: '#ed6c02',
                  '&:hover': { backgroundColor: '#e65100' },
                  fontWeight: 600
                }}
              >
                {t('add_new_game')}
              </Button>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button
                onClick={() => updateGameStatusBulk()}
                disabled={isSaving}
                variant="contained"
                fullWidth
                size="medium"
                startIcon={<SaveIcon />}
                sx={{
                  backgroundColor: '#9c27b0',
                  '&:hover': { backgroundColor: '#7b1fa2' },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)'
                  },
                  fontWeight: 600
                }}
              >
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Card sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
          <Tab label={t('single_player_mode')} value={'single'} />
          <Tab label={t('multi_player_mode')} value={'multi'} />
        </Tabs>
      </Card>
      <Grid container spacing={4}>
        {_.size(filteredList) > 0 ? (
          _.map(filteredList, (game) => (
            <GameListCard
              game={game}
              key={game._id}
              setIsGameActiveInList={setIsGameActiveInList}
            />
          ))
        ) : (
          <NoRecordItem grid />
        )}
      </Grid>
      <EditDialog
        title={t('add_game')}
        handleSave={formik.handleSubmit}
        open={dialogMode === 'add'}
        handleClose={() => setDialogMode(null)}
        sx={{ overflowY: 'hidden' }}
        confirmDisabled={!formik.isValid || formik.isSubmitting}
      >
        <TextField
          fullWidth
          label={t('name')}
          {...formikProps(formik, 'name')}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label={t('description')}
          {...formikProps(formik, 'description')}
          multiline
          rows={3}
          sx={{ mb: 2 }}
          placeholder={
            t('game_description_placeholder') ||
            'Enter game description (optional)'
          }
        />
        <TextField
          fullWidth
          label={t('order_number')}
          type="number"
          inputProps={{ min: 0, step: 1 }}
          {...formikProps(formik, 'order')}
          sx={{ mb: 2 }}
          helperText={t('order_number_help_text')}
        />
        <TextField
          fullWidth
          label={t('feature_start')}
          type="date"
          InputLabelProps={{ shrink: true }}
          name="featureStart"
          value={formatDateForDisplay(formik.values.featureStart)}
          onChange={(e) => formik.setFieldValue('featureStart', e.target.value)}
          error={
            formik.touched.featureStart && Boolean(formik.errors.featureStart)
          }
          helperText={formik.touched.featureStart && formik.errors.featureStart}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label={t('feature_end')}
          type="date"
          InputLabelProps={{ shrink: true }}
          name="featureEnd"
          value={formatDateForDisplay(formik.values.featureEnd)}
          onChange={(e) => formik.setFieldValue('featureEnd', e.target.value)}
          error={formik.touched.featureEnd && Boolean(formik.errors.featureEnd)}
          helperText={formik.touched.featureEnd && formik.errors.featureEnd}
          sx={{ mb: 2 }}
        />
        <SubjectSelector
          fullWidth
          sx={{ mb: 2 }}
          multiple
          selected={_.get(formik.values, 'subjects')}
          setSelected={(v) => formik.setFieldValue('subjects', v)}
        />

        <Autocomplete
          multiple
          fullWidth
          options={filterOptions}
          value={filterOptions.filter((option) =>
            formik.values.filters.includes(option._id)
          )}
          onChange={(event, newValue) => {
            formik.setFieldValue(
              'filters',
              newValue.map((option) => option._id)
            );
          }}
          getOptionLabel={(option) =>
            option.name?.zh || option.name?.en || option.name || ''
          }
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.name?.zh || option.name?.en || option.name || ''}
                {...getTagProps({ index })}
                key={option._id}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('grade_or_class')}
              placeholder={
                t('select_class_optional') || 'Select classes (optional)'
              }
              variant="outlined"
            />
          )}
          sx={{ mb: 2 }}
        />
        <TextField
          select
          {...formikProps(formik, 'gameType')}
          label={t('type')}
          sx={{ mb: 2, width: { xs: '100%', sm: '50%' }, pr: { xs: 0, sm: 2 } }}
        >
          <MenuItem value={'mc'}>
            <span>{t('game_mc')}</span>
            <span style={{ color: 'gray', paddingLeft: '8px' }}>
              {`( ${t('single_player_mode')} )`}
            </span>
          </MenuItem>
          <MenuItem value={'interactive'}>
            <span>{t('game_interactive')}</span>
            <span style={{ color: 'gray', paddingLeft: '8px' }}>
              {`( ${t('multi_player_mode')} )`}
            </span>
          </MenuItem>
        </TextField>
        <TextField
          select
          fullWidth
          {...formikProps(formik, 'isActive')}
          label={t('status')}
          sx={{ mb: 2, width: { xs: '100%', sm: '50%' } }}
        >
          <MenuItem value={true}> {t('active')} </MenuItem>
          <MenuItem value={false}> {t('inactive')} </MenuItem>
        </TextField>
        {hasPointConfig && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('point_correct')}
                type="number"
                inputProps={{ min: 0, step: 1 }}
                {...formikProps(formik, 'pointCorrect')}
                sx={{ md: { mb: 2 }, width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('point_incorrect')}
                type="number"
                inputProps={{ min: 0, step: 1 }}
                {...formikProps(formik, 'pointIncorrect')}
                sx={{ md: { mb: 2 }, width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('point_exchange_rate')}
                type="number"
                inputProps={{ min: 0, step: 1 }}
                {...formikProps(formik, 'pointExchangeRate')}
                sx={{ md: { mb: 2 }, width: '100%' }}
              />
            </Grid>
          </Grid>
        )}
      </EditDialog>
    </LayoutContainer>
  );
}

function CloseButton({ onClick = () => {} }) {
  return (
    <IconButton size="small" onClick={onClick} sx={{ ml: 1, p: 0.5 }}>
      <CloseIcon fontSize="small" color="error" />
    </IconButton>
  );
}
